import { useState } from 'react'
import {
  useEmailTemplates,
  useEmailTemplate,
  useUpdateEmailTemplate,
  useCreateEmailTemplate,
  usePreviewEmailTemplate,
  useResetEmailTemplate,
  type EmailTemplate,
} from '@/hooks/useEmailTemplates'
import { useAuthStore } from '@/store/authStore'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Mail, Pencil, Eye, RotateCcw, Copy, Check, Plus } from 'lucide-react'
import { toast } from 'sonner'

export function EmailTemplatesPage() {
  const { data, isLoading } = useEmailTemplates()
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)

  const templates = data?.templates || []

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <h1 className="text-2xl font-bold">Plantillas de Email</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Plantillas de Email</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gestiona las plantillas HTML de los emails transaccionales
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Crear Plantilla
        </Button>
      </div>

      {templates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Mail className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">No hay plantillas configuradas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <EmailTemplateCard
              key={t.id}
              template={t}
              onEdit={() => {
                setSelectedId(t.id)
                setEditOpen(true)
              }}
            />
          ))}
        </div>
      )}

      {selectedId && (
        <EmailTemplateEditDialog
          templateId={selectedId}
          open={editOpen}
          onClose={() => {
            setEditOpen(false)
            setSelectedId(null)
          }}
        />
      )}

      <EmailTemplateCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}

// ========================================
// Template Card
// ========================================

function EmailTemplateCard({ template, onEdit }: { template: EmailTemplate; onEdit: () => void }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-base">{template.name}</CardTitle>
          <Badge variant={template.is_active ? 'outline' : 'secondary'} className="text-xs">
            {template.is_active ? 'Activo' : 'Inactivo'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">{template.description || 'Sin descripcion'}</p>
        <div className="text-xs text-muted-foreground">
          Slug: <code className="rounded bg-muted px-1 py-0.5">{template.slug}</code>
        </div>
        <div className="truncate text-xs text-muted-foreground">Asunto: {template.subject}</div>
        {template.variables.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {template.variables.map((v) => (
              <Badge key={v} variant="outline" className="px-1.5 py-0 text-[10px]">
                {v}
              </Badge>
            ))}
          </div>
        )}
        <div className="flex gap-2 border-t pt-2">
          <Button variant="outline" size="sm" onClick={onEdit}>
            <Pencil className="mr-1 h-3 w-3" />
            Editar
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ========================================
// Edit Dialog
// ========================================

function EmailTemplateEditDialog({
  templateId,
  open,
  onClose,
}: {
  templateId: string
  open: boolean
  onClose: () => void
}) {
  const { data: template, isLoading } = useEmailTemplate(templateId)
  const updateTemplate = useUpdateEmailTemplate()
  const previewTemplate = usePreviewEmailTemplate()
  const resetTemplate = useResetEmailTemplate()
  const isSuperAdmin = useAuthStore((s) => s.user?.role === 'super_admin')

  const [formState, setFormState] = useState<{
    name?: string
    description?: string
    subject?: string
    body_html?: string
    is_active?: boolean
  }>({})
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)
  const [previewHtml, setPreviewHtml] = useState<string | null>(null)

  const name = formState.name ?? template?.name ?? ''
  const description = formState.description ?? template?.description ?? ''
  const subject = formState.subject ?? template?.subject ?? ''
  const bodyHtml = formState.body_html ?? template?.body_html ?? ''
  const isActive = formState.is_active ?? template?.is_active ?? true

  const handleSave = () => {
    const updates: Record<string, unknown> = {}
    if (formState.name !== undefined) updates.name = formState.name
    if (formState.description !== undefined) updates.description = formState.description
    if (formState.subject !== undefined) updates.subject = formState.subject
    if (formState.body_html !== undefined) updates.body_html = formState.body_html
    if (formState.is_active !== undefined) updates.is_active = formState.is_active

    if (Object.keys(updates).length === 0) {
      toast.info('No hay cambios para guardar')
      return
    }

    updateTemplate.mutate(
      { id: templateId, updates },
      {
        onSuccess: () => {
          setFormState({})
          onClose()
        },
      }
    )
  }

  const handlePreview = () => {
    previewTemplate.mutate(
      { id: templateId },
      {
        onSuccess: (data) => {
          setPreviewHtml(data.html)
        },
      }
    )
  }

  const handleReset = () => {
    if (!template) return
    resetTemplate.mutate(template.slug, {
      onSuccess: () => {
        setResetConfirmOpen(false)
        setFormState({})
      },
    })
  }

  const handleClose = () => {
    setFormState({})
    setPreviewHtml(null)
    onClose()
  }

  return (
    <>
      <Dialog open={open} onOpenChange={() => handleClose()}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isLoading ? 'Cargando...' : `Editar: ${template?.name}`}</DialogTitle>
          </DialogHeader>

          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          ) : (
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="general">General</TabsTrigger>
                <TabsTrigger value="subject">Asunto</TabsTrigger>
                <TabsTrigger value="html">HTML</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>

              {/* General Tab */}
              <TabsContent value="general" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="et-name">Nombre</Label>
                  <Input
                    id="et-name"
                    value={name}
                    onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Slug</Label>
                  <Input value={template?.slug || ''} disabled />
                  <p className="text-xs text-muted-foreground">
                    Identificador unico del sistema, no editable
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="et-desc">Descripcion</Label>
                  <Input
                    id="et-desc"
                    value={description}
                    onChange={(e) => setFormState((s) => ({ ...s, description: e.target.value }))}
                  />
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={isActive}
                    onCheckedChange={(v) => setFormState((s) => ({ ...s, is_active: v }))}
                  />
                  <Label>Activo</Label>
                </div>
                <div className="space-y-2">
                  <Label>Variables disponibles</Label>
                  <div className="flex flex-wrap gap-2">
                    {template?.variables?.map((v) => (
                      <VariableBadge key={v} variable={v} />
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Usa estas variables en el asunto o HTML con la sintaxis{' '}
                    <code className="rounded bg-muted px-1">{'{{variable}}'}</code>
                  </p>
                </div>
              </TabsContent>

              {/* Subject Tab */}
              <TabsContent value="subject" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="et-subject">Linea de asunto</Label>
                  <Input
                    id="et-subject"
                    value={subject}
                    onChange={(e) => setFormState((s) => ({ ...s, subject: e.target.value }))}
                  />
                  <p className="text-xs text-muted-foreground">
                    Soporta variables. Ejemplo:{' '}
                    <code className="rounded bg-muted px-1">
                      {'Hola {{user_name}}, nuevo inicio de sesion'}
                    </code>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Variables disponibles</Label>
                  <div className="flex flex-wrap gap-2">
                    {template?.variables?.map((v) => (
                      <VariableBadge key={v} variable={v} />
                    ))}
                  </div>
                </div>
              </TabsContent>

              {/* HTML Tab */}
              <TabsContent value="html" className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Contenido HTML del body</Label>
                    <div className="flex flex-wrap gap-1">
                      {template?.variables?.map((v) => (
                        <VariableBadge key={v} variable={v} />
                      ))}
                    </div>
                  </div>
                  <textarea
                    className="h-[500px] w-full resize-y rounded-md border bg-muted/50 p-3 font-mono text-xs"
                    value={bodyHtml}
                    onChange={(e) => setFormState((s) => ({ ...s, body_html: e.target.value }))}
                    spellCheck={false}
                  />
                  <p className="text-xs text-muted-foreground">
                    Este HTML se inserta dentro del layout base (header + footer compartidos). Usa{' '}
                    <code className="rounded bg-muted px-1">{'{{variable}}'}</code> para datos
                    dinamicos.
                  </p>
                </div>
              </TabsContent>

              {/* Preview Tab */}
              <TabsContent value="preview" className="space-y-4">
                <Button onClick={handlePreview} disabled={previewTemplate.isPending}>
                  <Eye className="mr-2 h-4 w-4" />
                  {previewTemplate.isPending ? 'Generando...' : 'Generar Preview'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  Genera un preview con datos de ejemplo. Muestra el email completo (layout + body).
                </p>
                {previewHtml && (
                  <div className="rounded-md border">
                    <iframe
                      srcDoc={previewHtml}
                      className="h-[500px] w-full rounded-md"
                      title="Email preview"
                      sandbox=""
                    />
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}

          <div className="flex items-center justify-between border-t pt-4">
            <div>
              {isSuperAdmin && template && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setResetConfirmOpen(true)}
                >
                  <RotateCcw className="mr-1 h-3 w-3" />
                  Restaurar default
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={updateTemplate.isPending}>
                {updateTemplate.isPending ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Restaurar plantilla</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Esto restaurara <strong>{template?.name}</strong> a su contenido original del sistema.
            Se perderan todos los cambios personalizados.
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setResetConfirmOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleReset} disabled={resetTemplate.isPending}>
              {resetTemplate.isPending ? 'Restaurando...' : 'Restaurar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ========================================
// Variable Badge (copiable)
// ========================================

function VariableBadge({ variable }: { variable: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(`{{${variable}}}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Badge
      variant="outline"
      className="cursor-pointer gap-1 text-xs hover:bg-muted"
      onClick={handleCopy}
      title="Click para copiar"
    >
      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      {variable}
    </Badge>
  )
}

// ========================================
// Create Dialog
// ========================================

function EmailTemplateCreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createTemplate = useCreateEmailTemplate()

  const [formState, setFormState] = useState({
    slug: '',
    name: '',
    description: '',
    subject: '',
    body_html: '',
    variables: '',
    is_active: true,
  })

  const handleCreate = () => {
    if (!formState.slug.trim()) {
      toast.error('El slug es requerido')
      return
    }
    if (!formState.name.trim()) {
      toast.error('El nombre es requerido')
      return
    }
    if (!formState.subject.trim()) {
      toast.error('El asunto es requerido')
      return
    }
    if (!formState.body_html.trim()) {
      toast.error('El contenido HTML es requerido')
      return
    }

    // Validate slug format
    const slugPattern = /^[a-z0-9-]+$/
    if (!slugPattern.test(formState.slug)) {
      toast.error('El slug solo puede contener letras minúsculas, números y guiones')
      return
    }

    const variables = formState.variables
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0)

    createTemplate.mutate(
      {
        slug: formState.slug.trim(),
        name: formState.name.trim(),
        description: formState.description.trim() || undefined,
        subject: formState.subject.trim(),
        body_html: formState.body_html.trim(),
        variables,
        is_active: formState.is_active,
      },
      {
        onSuccess: () => {
          setFormState({
            slug: '',
            name: '',
            description: '',
            subject: '',
            body_html: '',
            variables: '',
            is_active: true,
          })
          onClose()
        },
      }
    )
  }

  const handleClose = () => {
    setFormState({
      slug: '',
      name: '',
      description: '',
      subject: '',
      body_html: '',
      variables: '',
      is_active: true,
    })
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Crear Nueva Plantilla de Email</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="create-slug">
                Slug <span className="text-destructive">*</span>
              </Label>
              <Input
                id="create-slug"
                placeholder="mi-plantilla"
                value={formState.slug}
                onChange={(e) => setFormState((s) => ({ ...s, slug: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">
                Solo minúsculas, números y guiones. Ej: bienvenida-usuario
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-name">
                Nombre <span className="text-destructive">*</span>
              </Label>
              <Input
                id="create-name"
                placeholder="Mi Plantilla"
                value={formState.name}
                onChange={(e) => setFormState((s) => ({ ...s, name: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="create-desc">Descripción</Label>
            <Input
              id="create-desc"
              placeholder="Descripción breve de la plantilla"
              value={formState.description}
              onChange={(e) => setFormState((s) => ({ ...s, description: e.target.value }))}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="create-subject">
              Asunto <span className="text-destructive">*</span>
            </Label>
            <Input
              id="create-subject"
              placeholder="Asunto del email"
              value={formState.subject}
              onChange={(e) => setFormState((s) => ({ ...s, subject: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              Puedes usar variables como {'{'}
              {'{user_name}}'}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="create-variables">Variables</Label>
            <Input
              id="create-variables"
              placeholder="user_name, user_email, link"
              value={formState.variables}
              onChange={(e) => setFormState((s) => ({ ...s, variables: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              Lista separada por comas. Ej: user_name, reset_link
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="create-html">
              Contenido HTML <span className="text-destructive">*</span>
            </Label>
            <textarea
              id="create-html"
              className="h-[300px] w-full resize-y rounded-md border bg-muted/50 p-3 font-mono text-xs"
              placeholder="<h1>Hola {{user_name}}</h1>..."
              value={formState.body_html}
              onChange={(e) => setFormState((s) => ({ ...s, body_html: e.target.value }))}
              spellCheck={false}
            />
            <p className="text-xs text-muted-foreground">
              Este HTML se insertará dentro del layout base del sistema
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Switch
              checked={formState.is_active}
              onCheckedChange={(v) => setFormState((s) => ({ ...s, is_active: v }))}
            />
            <Label>Plantilla activa</Label>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button onClick={handleCreate} disabled={createTemplate.isPending}>
            {createTemplate.isPending ? 'Creando...' : 'Crear Plantilla'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
